import { css } from 'styled-components';

export const customPropertiesColors = css`
  // Color palette (base)
  --palette-base-100: #fff;
  --palette-base-200: #f6f6f6;
  --palette-base-300: #f0f0f0;
  --palette-base-400: #ddd;
  --palette-base-500: #707070;
  --palette-base-700: #484848;
  --palette-base-900: #282828;
  --palette-primary-100: #ecf6ff;
  --palette-primary-300: #d5edff;
  --palette-primary-500: #0057ff;
  --palette-primary-700: #00489b;
  --palette-info-100: #ecf6ff;
  --palette-info-300: #d5edff;
  --palette-info-500: #0057ff;
  --palette-info-700: #00489b;
  --palette-success-100: #f1fbf6;
  --palette-success-300: #ddf8eb;
  --palette-success-500: #00a962;
  --palette-success-700: #105e2d;
  --palette-warning-100: #fffae9;
  --palette-warning-300: #fff3c9;
  --palette-warning-500: #ea8c00;
  --palette-warning-700: #884a00;
  --palette-error-100: #fff3f1;
  --palette-error-300: #ffdeda;
  --palette-error-500: #f6432b;
  --palette-error-700: #980812;
  --palette-overlay-900-rgb: 40, 40, 40;
  --palette-overlay-100-rgb: 255, 255, 255;

  // Brand: Wayke
  ${(props) => props.theme.market.seRoot} {
    // Palette
    --palette-base-100: #fff;
    --palette-base-200: #f0f0f0;
    --palette-base-300: #e1e1e1;
    --palette-base-500: #707070;
    --palette-base-700: #424242;
    --palette-base-900: #181818;
    --palette-primary-100: #faeeed;
    --palette-primary-300: #fad5d5;
    --palette-primary-500: #ea576e;
    --palette-primary-700: #8f051a;
    --palette-accent-01-100: #dff3f7;
    --palette-accent-01-300: #bee3ee;
    --palette-accent-01-500: #00b6d2;
    --palette-accent-01-700: #022d3f;
    --palette-info-100: #e1f1ff;
    --palette-info-300: #b2d4f3;
    --palette-info-500: #3b76e7;
    --palette-info-700: #063084;
    --palette-success-100: #e5f7ed;
    --palette-success-300: #abd9c1;
    --palette-success-500: #448f67;
    --palette-success-700: #15452b;
    --palette-warning-100: #fff2e1;
    --palette-warning-300: #f9dbb6;
    --palette-warning-500: #fda32b;
    --palette-warning-700: #673c03;
    --palette-error-100: #ffe4e4;
    --palette-error-300: #ffbbbb;
    --palette-error-500: #eb2a2a;
    --palette-error-700: #760f0f;

    // Global colors overwrite
    --c-text-main: var(--palette-base-700);
    --c-secondary-bg: var(--palette-primary-100);
    --c-link: var(--palette-accent-01-500);
    --c-link-hover: var(--palette-accent-01-500);
    --c-input-accent: var(--palette-accent-01-500);
    --c-main-nav-bg: var(--palette-base-700);
    --c-alert-success-bg: var(--c-semantic-success-100);
    --c-alert-warning-bg: var(--c-semantic-warning-100);
    --c-alert-error-bg: var(--c-semantic-error-100);
    --c-alert-info-bg: var(--c-semantic-info-100);
    --c-global-search-item-bg: var(--palette-base-200);
  }

  // Global colors
  --c-bg-main: var(--palette-base-100);
  --c-bg-primary: var(--palette-primary-100);
  --c-text-main: var(--palette-base-900);
  --c-text-muted: var(--palette-base-500);
  --c-primary-bg: var(--palette-primary-500);
  --c-primary-fg: var(--palette-base-100);
  --c-secondary-bg: var(--palette-primary-300);
  --c-secondary-fg: var(--palette-primary-500);
  --c-base-bg: var(--palette-base-700);
  --c-base-fg: var(--palette-base-100);
  --c-contrast-bg: var(--palette-base-900);
  --c-contrast-fg: var(--palette-base-100);
  --c-accent-bg: var(--palette-base-200);
  --c-accent-fg: var(--palette-base-900);
  --c-link: var(--palette-base-900);
  --c-link-hover: var(--palette-base-700);
  --c-border-main: var(--palette-base-400);
  --c-border-alt: var(--palette-base-700);
  --c-overlay-rgb: var(--palette-overlay-900-rgb);

  --c-input-bg: var(--palette-base-200);
  --c-input-text: var(--palette-base-900);
  --c-input-label: var(--palette-base-500);
  --c-input-affix: var(--palette-base-500);
  --c-input-border: var(--palette-base-400);
  --c-input-border-active: var(--palette-base-700);
  --c-input-error-text: var(--palette-error-500);
  --c-input-error-border: var(--palette-error-500);
  --c-input-warning-text: var(--palette-warning-500);
  --c-input-warning-border: var(--palette-warning-500);
  --c-input-selection-bg: var(--palette-base-400);
  --c-input-selection-bg-active: var(--palette-base-700);
  --c-input-selection-bg-active-fg: var(--palette-base-100);

  --c-upload-button-bg: var(--palette-base-100);
  --c-upload-button-bg-last: var(--palette-base-300);

  --c-mainnav-bg: var(--palette-base-200);
  --c-mainnav-active-bg: var(--palette-primary-300);
  --c-mainnav-hover-bg: rgba(var(--palette-overlay-900-rgb), 0.05);
  --c-mainnav-overlay: rgba(var(--palette-overlay-900-rgb), 0.2);

  --c-global-search-bg: var(--palette-base-100);
  --c-global-search-fg: var(--palette-base-900);
  --c-global-search-overlay: rgba(var(--palette-overlay-900-rgb), 0.2);
  --c-global-search-item-bg: var(--palette-base-300);

  --c-tabs-bg: var(--palette-base-100);
  --c-tabs-fg: var(--palette-base-900);
  --c-tabs-active-bg: var(--palette-base-900);
  --c-tabs-active-fg: var(--palette-base-100);
  --c-tabs-hover-bg: var(--palette-base-200);

  --c-main-nav-bg: var(--palette-base-900);
  --c-main-nav-fg: var(--palette-base-100);
  --c-main-nav-overlay: rgba(var(--palette-overlay-100-rgb), 0.1);
  --c-skeleton: var(--palette-base-300);
  --c-transparent-chess-pattern: var(--palette-base-300);
  --c-focus: var(--palette-info-500);
  --c-panellum-action-bg: var(--palette-base-900);
  --c-panellum-action-bg-hover: var(--palette-base-700);
  --c-panellum-action-fg: var(--palette-base-100);
  --c-panellum-overlay: rgba(var(--palette-overlay-100-rgb), 0.3);
  --c-quick-menu-highlight: var(--palette-info-500);
  --c-semantic-success-100: var(--palette-success-100);
  --c-semantic-success-300: var(--palette-success-300);
  --c-semantic-success-500: var(--palette-success-500);
  --c-semantic-success-700: var(--palette-success-700);
  --c-semantic-warning-100: var(--palette-warning-100);
  --c-semantic-warning-300: var(--palette-warning-300);
  --c-semantic-warning-500: var(--palette-warning-500);
  --c-semantic-warning-700: var(--palette-warning-700);
  --c-semantic-error-100: var(--palette-error-100);
  --c-semantic-error-300: var(--palette-error-300);
  --c-semantic-error-500: var(--palette-error-500);
  --c-semantic-error-700: var(--palette-error-700);
  --c-semantic-info-100: var(--palette-info-100);
  --c-semantic-info-300: var(--palette-info-300);
  --c-semantic-info-500: var(--palette-info-500);
  --c-semantic-info-700: var(--palette-info-700);
  --c-semantic-neutral-100: var(--palette-base-100);
  --c-semantic-neutral-300: var(--palette-base-300);
  --c-semantic-neutral-500: var(--palette-base-500);
  --c-semantic-neutral-700: var(--palette-base-700);

  --c-alert-success-bg: var(--c-semantic-success-300);
  --c-alert-success-icon-bg: var(--c-semantic-success-500);
  --c-alert-success-icon-fg: var(--palette-base-100);
  --c-alert-warning-bg: var(--c-semantic-warning-300);
  --c-alert-warning-icon-bg: var(--c-semantic-warning-500);
  --c-alert-warning-icon-fg: var(--palette-base-100);
  --c-alert-error-bg: var(--c-semantic-error-300);
  --c-alert-error-icon-bg: var(--c-semantic-error-500);
  --c-alert-error-icon-fg: var(--palette-base-100);
  --c-alert-info-bg: var(--c-semantic-info-300);
  --c-alert-info-icon-bg: var(--c-semantic-info-500);
  --c-alert-info-icon-fg: var(--palette-base-100);

  --c-calendar-current-day-bg: var(--palette-error-500);
  --c-calendar-current-day-fg: var(--palette-base-100);

  // Constants
  --c-facebook-blue: #1677f1;
`;
